/**
 * <nf-bfcm-signup>: early-access landing for Black Friday and Singles' Day (sections/nf-bfcm-signup.liquid).
 * Countdown, form validation, mobile bar, and the Klaviyo client sign-up.
 * Hand-authored (no scripts/ source), loaded by the section only.
 * Canvases: prompts/BFCM_SIGNUP.md, prompts/SINGLES_DAY_SIGNUP.md.
 */
(() => {
  'use strict';

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const E164 = /^\+[1-9]\d{7,14}$/;
  const KLAVIYO = 'https://a.klaviyo.com/client/subscriptions/';
  const REVISION = '2024-10-15';
  const pad = (n) => String(n).padStart(2, '0');

  class NfBfcmSignup extends HTMLElement {
    #config = {};
    #timer = 0;
    #observer = null;

    connectedCallback() {
      try {
        this.#config = JSON.parse(this.dataset.config || '{}');
      } catch {
        this.#config = {};
      }
      this.#startCountdown();
      this.#watchHeroForm();
      for (const form of this.querySelectorAll('[data-bfcm-form]')) this.#wireForm(form);
      this.querySelector('[data-bfcm-jump]')?.addEventListener('click', () => this.#jumpToFooterForm());
    }

    disconnectedCallback() {
      clearInterval(this.#timer);
      this.#observer?.disconnect();
    }

    /* ── Countdown ── */

    #startCountdown() {
      const end = Date.parse(this.#config.deadline || '');
      if (Number.isNaN(end)) return;
      const tick = () => {
        const left = Math.max(0, end - Date.now());
        const s = Math.floor(left / 1000);
        const parts = {
          days: Math.floor(s / 86400),
          hours: Math.floor((s % 86400) / 3600),
          minutes: Math.floor((s % 3600) / 60),
          seconds: s % 60,
        };
        for (const el of this.querySelectorAll('[data-bfcm-unit]')) {
          el.textContent = pad(parts[el.dataset.bfcmUnit]);
        }
        for (const el of this.querySelectorAll('[data-bfcm-countdown-text]')) {
          el.textContent = `${parts.days}d ${pad(parts.hours)}h ${pad(parts.minutes)}m ${pad(parts.seconds)}s`;
        }
        const label = this.#config.strings.timer
          .replace('__D__', parts.days)
          .replace('__H__', parts.hours)
          .replace('__M__', parts.minutes);
        for (const el of this.querySelectorAll('[data-bfcm-countdown]')) el.setAttribute('aria-label', label);
      };
      tick();
      this.#timer = setInterval(tick, 1000);
    }

    /* ── Mobile bar: shown only while the hero form is off screen ── */

    #watchHeroForm() {
      const hero = this.querySelector('[data-bfcm-hero-form]');
      const bar = this.querySelector('[data-bfcm-bar]');
      if (!hero || !bar) return;
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
          bar.toggleAttribute('inert', entry.isIntersecting);
        },
        { root: this.classList.contains('nf-bfcm--takeover') ? this : null },
      );
      this.#observer.observe(hero);
    }

    #jumpToFooterForm() {
      const wrap = this.querySelector('[data-bfcm-footer-form]');
      if (!wrap) return;
      const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
      wrap.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
      wrap.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    }

    /* ── Form ── */

    #wireForm(form) {
      const messages = form.querySelector('[data-bfcm-messages]');
      const phoneWrap = form.querySelector('[data-bfcm-phone-wrap]');
      messages?.addEventListener('change', () => {
        phoneWrap.hidden = !messages.checked;
        if (!messages.checked) this.#setError(form, 'phone', '');
      });
      form.querySelector('[data-bfcm-email]')?.addEventListener('blur', (event) => {
        if (event.target.value.trim()) this.#setError(form, 'email', this.#emailProblem(event.target.value.trim()));
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#submit(form);
      });
    }

    #emailProblem(email) {
      const { strings } = this.#config;
      if (!email) return strings.emailEmpty;
      if (email.length > 254 || !EMAIL.test(email)) return strings.emailInvalid;
      return '';
    }

    /** `0…` takes the default dial code, `00…` becomes `+…`; anything else must already be `+…`. */
    #normalizePhone(raw, defaultCode) {
      const compact = raw.replace(/[\s().-]/g, '');
      if (compact.startsWith('00')) return `+${compact.slice(2)}`;
      if (compact.startsWith('0') && defaultCode) return `${defaultCode}${compact.slice(1)}`;
      return compact;
    }

    #setError(form, field, message) {
      const el = form.querySelector(`[data-bfcm-error="${field}"]`);
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
      const input = field === 'email' ? form.querySelector('[data-bfcm-email]') : form.querySelector('[data-bfcm-phone]');
      if (input) {
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }

    async #submit(form) {
      const { strings, companyId, listId } = this.#config;
      const data = new FormData(form);
      const email = String(data.get('email') || '').trim().toLowerCase();
      const firstName = String(data.get('firstName') || '').replace(/[\p{C}<>]/gu, '').trim().slice(0, 50);
      const newsletter = data.get('newsletter') === 'yes';
      const wantsMessages = data.get('messages') === 'yes';
      const phone = wantsMessages ? this.#normalizePhone(String(data.get('phone') || ''), form.dataset.defaultCode) : '';

      const emailProblem = this.#emailProblem(email);
      let phoneProblem = '';
      if (wantsMessages) phoneProblem = !phone ? strings.phoneEmpty : E164.test(phone) ? '' : strings.phoneInvalid;
      this.#setError(form, 'email', emailProblem);
      this.#setError(form, 'phone', phoneProblem);
      this.#setError(form, 'form', '');
      if (emailProblem || phoneProblem) {
        (emailProblem ? form.querySelector('[data-bfcm-email]') : form.querySelector('[data-bfcm-phone]'))?.focus();
        return;
      }

      // Bots fill every field. They get the success screen and nothing is sent.
      const bot = String(data.get('company') || '') !== '';
      const button = form.querySelector('[type="submit"]');
      button.disabled = true;
      button.textContent = button.dataset.busyLabel;

      try {
        if (!bot) {
          if (!companyId || !listId) throw new Error('Klaviyo is not configured');
          await this.#subscribe(listId, { email, firstName, phone, newsletter });
          if (newsletter && this.#config.newsletterListId) {
            await this.#subscribe(this.#config.newsletterListId, { email, firstName, phone, newsletter });
          }
          this.#trackLead();
        }
        this.#showDone(form, firstName);
      } catch {
        this.#setError(form, 'form', strings.generic);
        button.disabled = false;
        button.textContent = button.dataset.label;
      }
    }

    async #subscribe(list, { email, firstName, phone, newsletter }) {
      const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      if (phone) subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      // Campaign tag from the section's `campaign` setting, e.g. bfcm_2026 / bfcm_2026_newsletter.
      const { profileProperty, customSource } = this.#config;
      const attributes = {
        email,
        subscriptions,
        properties: { [profileProperty]: true, [`${profileProperty}_newsletter`]: newsletter },
      };
      if (firstName) attributes.first_name = firstName;
      if (phone) attributes.phone_number = phone;

      const response = await fetch(`${KLAVIYO}?company_id=${encodeURIComponent(this.#config.companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: customSource,
              profile: { data: { type: 'profile', attributes } },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        }),
      });
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
    }

    #trackLead() {
      // No form values: the event only carries the source.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'generate_lead', lead_source: this.#config.leadSource });
    }

    #showDone(form, firstName) {
      const done = form.parentElement.querySelector('[data-bfcm-done]');
      const { strings } = this.#config;
      done.querySelector('[data-bfcm-done-title]').textContent = firstName
        ? strings.successName.replace('__NAME__', firstName)
        : strings.success;
      form.hidden = true;
      done.hidden = false;
      done.focus();
    }
  }

  if (!customElements.get('nf-bfcm-signup')) customElements.define('nf-bfcm-signup', NfBfcmSignup);
})();
